import React from 'react';

import CapitalizedButton from '@modules/custom-grouping/components/common/CapitalizedButton';
import { ConditionalRendering } from '@common-components/conditional-rendering';
import withModalWrapper from '../../common/WithModalWrapper';
import { modalConfig } from '@modules/custom-grouping/modals/modalConfig';
import { useModal, useAppSelector, useExchangeConditions, useAppDispatch } from '@hooks/index';
import { formatToLocale } from '@utils/dates/dateMethods';
import useEnrollmentHistory from '../hooks/useEnrollmentHistory';
import { environmentSelector } from '@selectors/commonSelectors';
import { FROM_TAB, PLAN_TYPE } from '@modules/custom-grouping/modals/modalTypes';
import { IEnrollmentDetails } from './enrollmentHistoryTypes';
import { RELATIONSHIP_TYPES } from '@constants/common/relationShipTypes';
import { MemberRemovalReason } from '@components/ssap/SsapTypes';

export interface IDisernrollParams {
    checked?: boolean;
    coverageStartDate?: string;
    'reasonCode': string,
    'otherReason': string,
    'caseNumber': string,
    'planType': string,
    'enrollmentId': string
    'terminationDateChoice': string,
    'memberGuidsToDisenroll': string[],
    'isMemberLevelDisenrollEnabled': boolean,

}

const ActionButtonContainer = (props: { enrollmentDetails: IEnrollmentDetails, coverageStartDateForDisenrollment: string, coverageYear: string }) => {
    const { language, flags: { isMemberLevelDisenrollEnabled }, localDates } = useAppSelector(environmentSelector);
    const { enrollmentDetails, coverageStartDateForDisenrollment, coverageYear } = props;
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const {
        checkForPendingApplication,
        getTerminationBtnVisibility,
        getPayNowBtnVisibility,
        getDisEnrollBtnVisibility,
        payForPlan,
        changeTerminationDate,
        getEnrollees,
        getTerminationDates,
        getCancelBtnVisibility,
        submitDisEnrollment
    } = useEnrollmentHistory(enrollmentDetails, dispatch);
    const { isAreYouSureVisible, isWeHaveReviewedVisible } = useExchangeConditions();
    const self = enrollmentDetails.enrolleeHistoryEntries.find(enrolleeDetails => enrolleeDetails.relationship.toUpperCase() === RELATIONSHIP_TYPES.SELF.value);
    let isCancelCoverage = false;

    let disenrollParams: IDisernrollParams = {
        'reasonCode': '',
        'otherReason': '',
        'caseNumber': enrollmentDetails.caseNumber,
        'planType': enrollmentDetails.planType,
        'enrollmentId': enrollmentDetails.enrollmentId,
        'terminationDateChoice': '',
        'memberGuidsToDisenroll': [],
        'isMemberLevelDisenrollEnabled': isMemberLevelDisenrollEnabled
    };

    const showAllMembersText = () => {
        const currentDate = new Date(coverageStartDateForDisenrollment);
        return !enrollmentDetails.enrolleeHistoryEntries.some(enrollee =>
            new Date(enrollee.startDate) > currentDate && enrollee.relationship.toUpperCase() != RELATIONSHIP_TYPES.SELF.value
        );
    };

    const isDentalPlanType = enrollmentDetails.planType.toUpperCase() === PLAN_TYPE.DENTAL;

    function payNowClickHandler() {
        const payDetails = {
            caseNumber: enrollmentDetails.caseNumber,
            typeOfPlan: PLAN_TYPE[enrollmentDetails.planType.toUpperCase()],
            enrollmentId: enrollmentDetails.enrollmentId
        };
        payForPlan(
            payDetails,
            openModal
        );
    }

    const editTerminationDate = () => {
        const currentDate = new Date(localDates.serverDate);
        let terminationDates: string[] = [];
        const startDate = currentDate > new Date(enrollmentDetails.coverageStartDate) ? currentDate : new Date(enrollmentDetails.coverageStartDate);
        const endDate = new Date(enrollmentDetails.coverageEndDate);

        const startMonth = startDate.getMonth();
        const endMonth = endDate.getMonth();
        const year = endDate.getFullYear();

        for (let i = startMonth; i < endMonth; i++) {
            const lastDay = new Date(year, i + 1, 0);
            const formatedDated = formatToLocale(lastDay, language, 'MM/DD/YYYY');
            terminationDates.push(formatedDated);
        }
        return terminationDates;
    };

    const setTerminationDateHandler = (terminationDate: string): void => {
        const params = {
            'healthEnrollmentId': enrollmentDetails.enrollmentId,
            'caseNumber': enrollmentDetails.caseNumber,
            'reasonCode': MemberRemovalReason.OTHER,
            'coverageStartDate': enrollmentDetails.coverageStartDate,
            'terminationDate': terminationDate
        };
        changeTerminationDate(params, openModal, coverageYear, language);
    };

    const setMemberGuidsToDisenroll = (memberGuidsToDisenroll: string[]): void => {
        disenrollParams = { ...disenrollParams, memberGuidsToDisenroll };
        openModal('disenrollOptions', { typeOfPlan: enrollmentDetails.planType.toUpperCase(), enrollmentAction: 'submit', fromTab: FROM_TAB.ENROLLMENTHISTORY, setSelectedReason: setSelectedReason });
    };

    const setSelectedReason = (disenrollmentState) => {
        disenrollParams = { ...disenrollParams, 'reasonCode': disenrollmentState.reason, 'otherReason': disenrollmentState.reasonOther };
        if (isCancelCoverage) {
            onSubmitDisEnrollment();
        }
        else if (isAreYouSureVisible) {
            openModal('areYouSure', { hasEnrollmentAction: true, typeOfPlan: enrollmentDetails.planType.toUpperCase(), 'goToDisenrollDateModal': goToDisenrollDateModal });
        } else {
            goToDisenrollDateModal();
        }
    };

    const goToDisenrollDateModal = (): void => {
        const selfIdentifier = self?.exchgIndivIdentifier || '';

        // Check if the disenrollment action involves only the user
        if (disenrollParams.memberGuidsToDisenroll.length === 1) {
            const isSelf = disenrollParams.memberGuidsToDisenroll.includes(selfIdentifier);
            if (isSelf) {
                disenrollParams.memberGuidsToDisenroll = [];
            }
        }

        // Fetch eligible disenrollment dates
        const eligibleDisenrollmentDates = getTerminationDates(disenrollParams, coverageStartDateForDisenrollment);

        // Open modal with disenrollment options
        openModal('disEnrollHistoryOptions', {
            actualDatesEndOfCurrentMonth: eligibleDisenrollmentDates.endOfCurrentMonth,
            actualDatesEndOfNextMonth: eligibleDisenrollmentDates.endOfNextMonth,
            actualDatesEndOfMonthAfterNext: eligibleDisenrollmentDates.endOfMonthAfterNext,
            disenrollGroup: {
                planName: enrollmentDetails.planName,
                logoUrl: enrollmentDetails.planLogoUrl,
            },
            onSubmitDisEnrollment,
        });
    };

    const onSubmitDisEnrollment = (disenrollOptionSelected: string = '') => {
        disenrollParams.terminationDateChoice = disenrollOptionSelected;
        const REVIEWED_LABEL = 'indportal.plansummary.wehavereviewed';
        const CANCEL_COVERAGE_LABEL = 'indportal.plansummary.wehavereviewedcancelcoverage';
        const actionLabel = (isWeHaveReviewedVisible || isCancelCoverage) ? CANCEL_COVERAGE_LABEL : REVIEWED_LABEL;
        submitDisEnrollment(disenrollParams, openModal, isCancelCoverage, actionLabel, coverageYear, language);
    };

    const getDisenrollAlert = async (isCancelCoverageAction = false) => {
        try {
            isCancelCoverage = isCancelCoverageAction;

            // Check for pending application
            const response = await checkForPendingApplication({
                coverageYear: new Date(coverageStartDateForDisenrollment).getFullYear(),
            });

            const message = response?.data?.message;
            if (message?.toUpperCase() === 'TRUE') {
                // Open modal if there's a pending application
                openModal('pendingAppModal');
                return;
            }

            // Handle scenarios based on `isCancelCoverage` and feature flags
            if (isCancelCoverage) {
                handleCancelCoverage();
            } else if (isMemberLevelDisenrollEnabled) {
                handleMemberLevelDisenroll();
            } else {
                handleStandardDisenroll();
            }
        } catch (error) {
            // Handle errors gracefully
            openModal('disenrollFailureModal');
        }
    };

    // Helper function for cancel coverage flow
    const handleCancelCoverage = () => {
        disenrollParams['memberGuidsToDisenroll'] = [];
        openModal('disenrollOptions', {
            typeOfPlan: enrollmentDetails.planType.toUpperCase(),
            enrollmentAction: 'cancel',
            fromTab: FROM_TAB.ENROLLMENTHISTORY,
            setSelectedReason: setSelectedReason,
        });
    };

    // Helper function for member-level disenrollment
    const handleMemberLevelDisenroll = () => {
        openModal('disenrollMembers', {
            enrolles: getEnrollees(coverageStartDateForDisenrollment),
            showAllMembersText: showAllMembersText(),
            setMemberGuidsToDisenroll: setMemberGuidsToDisenroll,
            subscriberName: self?.name,
        });
    };

    // Helper function for standard disenrollment
    const handleStandardDisenroll = () => {
        openModal('disenrollOptions', {
            typeOfPlan: enrollmentDetails.planType.toUpperCase(),
            enrollmentAction: 'submit',
            fromTab: FROM_TAB.ENROLLMENTHISTORY,
            setSelectedReason: setSelectedReason,
        });
    };

    return (
        <>
            <div className='col-xs-12 margin-4 margin-t-0 d-flex gap-4'>
                <ConditionalRendering isTrue={getTerminationBtnVisibility()}>
                    <CapitalizedButton id="terminationDate" data-testid="terminationDate" labelId='indportal.enrollments.terminationDate' variant='primary' onClickHandler={() => openModal('changeTerminationDate', { 'terminationDateOptions': editTerminationDate(), 'setTerminationDate': setTerminationDateHandler })} />
                </ConditionalRendering>
                <ConditionalRendering isTrue={getPayNowBtnVisibility()}>
                    <CapitalizedButton id="payForPlan" data-testid="payForPlan" labelId={isDentalPlanType ? 'indportal.enrollments.payForDentalPlan' : 'indportal.enrollments.payForHealthPlan'} variant='secondary' onClickHandler={payNowClickHandler} />
                </ConditionalRendering>
                <ConditionalRendering isTrue={enrollmentDetails.allowDisenroll}>
                    <ConditionalRendering isTrue={getDisEnrollBtnVisibility(localDates) != null && getDisEnrollBtnVisibility(localDates)}>
                        <CapitalizedButton id="disenrollFromPlan" data-testid={isDentalPlanType ? 'disenrollFromPlan_Dental_01' : 'disenrollFromPlan_Health_01'} labelId={isDentalPlanType ? 'indportal.enrollments.disenrollFromDentalPlan' : 'indportal.enrollments.disenrollFromHealthPlan'} variant='primary'
                            onClickHandler={() => getDisenrollAlert()}
                        />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={getCancelBtnVisibility(localDates)}>
                        <CapitalizedButton id="cancelEnrollment" data-testid={isDentalPlanType ? 'disenrollFromPlan_Dental_02' : 'disenrollFromPlan_Health_02'} labelId={'indportal.plansummary.cancelcoverage'} variant='primary'
                            onClickHandler={() => getDisenrollAlert(true)} />
                    </ConditionalRendering>
                </ConditionalRendering>
            </div>
        </>
    );
};

export default withModalWrapper(ActionButtonContainer, modalConfig);